import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  View,
  ActivityIndicator,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSeatMap,
  selectSeat,
  selectSeatRebook,
  unselectSeatRebook,
  unselectSeat,
  setActiveSegment,
  selectSeatEvents,
  unselectSeatEvents,
} from '../store/actions/seat';
import {
  selectAirSegments,
  selectTravelCart,
  selectCheckoutStepperSteps,
  selectTravelCartPassengers,
} from '../store/selectors/trip.selector';
import PickerSelect from '../components/PickerSelect';
import FixedNextButtonBar from '../components/shared/FixedNextButtonBar';
import CheckoutStepper from '../components/CheckoutStepper';
import { Screens } from '../constants/ScreenNames';
import { Color, CommonProperties, CommonStyles } from '../styles';
import SeatUnit from '../components/SeatMap/SeatUnit';
import BaseText from '../components/BaseText';
import {
  selectActiveSeatMapNotAvailable,
  selectSeatSelectionBySeatNumber,
  selectActiveSegmentId,
  selectSeatMaps,
  selectTotalSeatPrice,
  selectActiveSeatSegment,
  selectSeatSelection,
  selectActiveSeatMap,
} from '../store/selectors/seat.selector';
import TotalPrice from '../components/TotalPrice';
import RowView from '../components/RowView';
import { scale } from '../utils/scale.utils';
import { useComponentTKey, i18nNS } from '../i18n';
import { useTranslation } from 'react-i18next';
import { selectTotalCartAmount } from '../store/selectors/cart.selector';
import { selectCheckedRebookPassengers } from '../store/selectors/mytrips.selector';
import { AA_ENABLED, EVENTS_ENABLED, REBOOK_ENABLED } from '../config/env';
import { updatePassengersSeats } from '../store/actions/search';
import { pageKeys } from '../constants/PageKeys';
import Messages from '../components/event/Messages';
import { selectTezEvent, selectTezEventGroup } from '../store/selectors/events.selector';
import {
  selectCompanyCurrency,
  selectTokenData,
  selectCompanyCurrencyCulture,
} from '../store/selectors/auth.selector';
import { formatCurrency } from '../utils/currency.utils';

const seatRowHeight = scale(42);
const getSeatItemLayout = (data, index) => ({
  length: seatRowHeight,
  offset: seatRowHeight * index,
  index,
});

const seatRowKeyExtractor = (seatRow) => seatRow.Number.toString();

const SeatScreen = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(i18nNS.checkout);
  const { tKey } = useComponentTKey('SeatScreen');

  const seatMapNotAvailable = useSelector(selectActiveSeatMapNotAvailable);
  const airSegments = useSelector(selectAirSegments);
  const activeSegment = useSelector(selectActiveSeatSegment);
  const activeSeatMap = useSelector(selectActiveSeatMap);
  const seatMaps = useSelector(selectSeatMaps);
  const seatSelection = useSelector(selectSeatSelection);
  const seatSelectionBySeatNumber = useSelector(selectSeatSelectionBySeatNumber);
  const currentSegmentIndex = useSelector(selectActiveSegmentId);
  const cartTotal = useSelector(selectTotalCartAmount);
  const seatTotal = useSelector(selectTotalSeatPrice);
  const travelCart = useSelector(selectTravelCart);
  const checkoutSteps = useSelector(selectCheckoutStepperSteps);
  const rebookPassengers = useSelector(selectCheckedRebookPassengers);
  const eventsPassengersList = useSelector(selectTravelCartPassengers);
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tezEvent = useSelector(selectTezEvent);
  const tokenData = useSelector(selectTokenData);
  const currency = useSelector(selectCompanyCurrency);
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const [seatLoading, setSeatLoading] = useState(false);
  const [currentPassengerIndex] = useState(0);
  const [activePassenger, setActivePassenger] = useState();
  const [eventsActivePassenger, setEventsActivePassenger] = useState();
  const [multipleSelectSamePassenger, setMultipleSelectSamePassenger] = useState(false);
  const passengerDataForDropdown =
    REBOOK_ENABLED &&
    rebookPassengers
      ?.filter((pax) => pax.PassengerType !== 'INF')
      ?.map((passenger) => {
        return {
          label: passenger.FirstName + ' ' + passenger.LastName,
          value: passenger.FirstName + '_' + passenger.LastName,
        };
      });
  const eventsPassengerDataForDropdown =
    EVENTS_ENABLED &&
    eventsPassengersList
      ?.filter((pax) => pax.PassengerType !== 'INF')
      ?.map((passenger) => {
        return {
          label: passenger.FirstName + ' ' + passenger.LastName,
          value: passenger.FirstName + '_' + passenger.LastName,
        };
      });
  useEffect(() => {
    dispatch(setActiveSegment(airSegments[0]?.SegmentId));
    fetchSeatMaps(airSegments[0]);
  }, [dispatch]);

  useEffect(() => {
    if (activePassenger === undefined) {
      setActivePassenger(passengerDataForDropdown[0]?.value);
    }
  }, [activePassenger, passengerDataForDropdown]);
  useEffect(() => {
    if (eventsActivePassenger === undefined) {
      setEventsActivePassenger(eventsPassengerDataForDropdown[0]?.value);
    }
  }, [eventsActivePassenger, eventsPassengerDataForDropdown]);

  const passengerNamesKeys =
    (REBOOK_ENABLED || EVENTS_ENABLED) &&
    seatSelection[currentSegmentIndex] &&
    Object.keys(seatSelection[currentSegmentIndex]);
  const getLabelForPassenger = (passenger) => {
    const passengerData = passengerNamesKeys?.find((key) => key.includes(passenger));
    return passengerData ? passengerData?.split('_')[0] : passenger;
  };
  const fetchSeatMaps = async (segment) => {
    if (!seatMaps || !seatMaps[segment.SegmentId]) {
      setSeatLoading(true);
      await dispatch(getSeatMap(segment));
      setSeatLoading(false);
    }
  };
  const currentSelectedSeat =
    (REBOOK_ENABLED || EVENTS_ENABLED) &&
    passengerNamesKeys?.map((passenger) => {
      return {
        name: passenger,
        seatNo: seatSelection[currentSegmentIndex]?.[passenger]?.SeatNumber,
      };
    });
  const seatSelectionHandler = useCallback(
    (seat, classOfService) => {
      if (seat.IsAvailable && !seat.isNotSelectable) {
        if (REBOOK_ENABLED) {
          const currentActiveSeat = currentSelectedSeat?.find(
            (active) => active.seatNo === seat.SeatNumber
          );
          if (
            seatSelection[currentSegmentIndex] &&
            seatSelection[currentSegmentIndex][activePassenger]?.SeatNumber === seat.SeatNumber
          ) {
            dispatch(
              unselectSeatRebook(seat, currentSegmentIndex, activePassenger, classOfService)
            );
          } else if (currentActiveSeat) {
            setMultipleSelectSamePassenger(true);
          } else {
            dispatch(
              selectSeatRebook(
                seat,
                currentSegmentIndex,
                activePassenger ?? passengerDataForDropdown[0]?.value,
                classOfService
              )
            );
          }
        } else if (EVENTS_ENABLED) {
          {
            const currentActiveSeat = currentSelectedSeat?.find(
              (active) => active.seatNo === seat.SeatNumber
            );
            if (
              seatSelection[currentSegmentIndex] &&
              seatSelection[currentSegmentIndex][eventsActivePassenger]?.SeatNumber ===
                seat.SeatNumber
            ) {
              dispatch(
                unselectSeatEvents(seat, currentSegmentIndex, eventsActivePassenger, classOfService)
              );
            } else if (currentActiveSeat) {
              setMultipleSelectSamePassenger(true);
            } else {
              dispatch(
                selectSeatEvents(
                  seat,
                  currentSegmentIndex,
                  eventsActivePassenger ?? eventsPassengerDataForDropdown[0]?.value,
                  classOfService
                )
              );
            }
          }
        } else {
          if (
            seatSelection[currentSegmentIndex] &&
            seatSelection[currentSegmentIndex][currentPassengerIndex]?.SeatNumber ===
              seat.SeatNumber
          ) {
            dispatch(
              unselectSeat(seat, currentSegmentIndex, currentPassengerIndex, classOfService)
            );
          } else {
            dispatch(selectSeat(seat, currentSegmentIndex, currentPassengerIndex, classOfService));
          }
        }
      }
    },
    [
      dispatch,
      currentSegmentIndex,
      currentPassengerIndex,
      seatSelection,
      passengerDataForDropdown,
      activePassenger,
      eventsPassengerDataForDropdown,
      eventsActivePassenger,
    ]
  );

  const segmentSelectionHandler = (segment) => {
    dispatch(setActiveSegment(segment.SegmentId));
    fetchSeatMaps(segment);
  };

  const isSeatSelected = (seat) =>
    seatSelectionBySeatNumber &&
    seatSelectionBySeatNumber[currentSegmentIndex] &&
    seatSelectionBySeatNumber[currentSegmentIndex][seat.SeatNumber];

  const SeatRow = memo(
    ({ item }) => {
      return (
        <View
          style={[
            styles.seatRow,
            item.newService ? styles.setRowNewService : null,
            item.Characterstics === 1 ? styles.exitRow : null,
          ]}
        >
          {item.Seats.map((seat, index) =>
            !seat.isAisleFiller ? (
              <SeatUnit
                key={seat.SeatNumber}
                isSelected={isSeatSelected(seat)}
                seat={seat}
                classOfService={item.classOfService}
                onSeatSelect={seatSelectionHandler}
              />
            ) : (
              <View key={index} style={styles.aisleFiller}></View>
            )
          )}
        </View>
      );
    },
    (prev, next) => prev.items !== next.items
  );

  const renderSeatRow = ({ item }) => <SeatRow item={item} />;
  return (
    <SafeAreaView style={styles.flex1}>
      <Messages
        companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
        groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
        pageKey={pageKeys.SEAT_SELECTION}
      />
      <CheckoutStepper
        entities={checkoutSteps}
        isTouchable={false}
        flowDepth={1}
        borderRadius={5}
        crumbsContainerStyle={styles.stepperContainer}
      />
      <View style={styles.priceContainer}>
        <View style={styles.totalPriceContainer}>
          <TotalPrice value={cartTotal} navigation={props.navigation} />
        </View>
      </View>

      {AA_ENABLED ? (
        <>
          <View style={styles.seatTypeSection}>
            <View style={[styles.box, styles.mainCabinExtra]}></View>
            <View>
              <BaseText style={CommonProperties.h3}>Main Cabin Extra</BaseText>
              <BaseText>Extra Legroom</BaseText>
              <BaseText>Preferred Boarding</BaseText>
            </View>
            <View style={[styles.box, styles.preferred]}></View>
            <View>
              <BaseText style={CommonProperties.h3}>Preferred</BaseText>
              <BaseText>Standard Legroom</BaseText>
              <BaseText>Favourable Location</BaseText>
            </View>
          </View>
          <View style={styles.seatTypeSection}>
            <View style={[styles.box, styles.available]}></View>
            <View>
              <BaseText style={CommonProperties.h3}>Available</BaseText>
            </View>
            <View style={[styles.box, styles.unavailable]}></View>
            <View>
              <BaseText style={CommonProperties.h3}>Unavailable</BaseText>
            </View>
          </View>
        </>
      ) : null}

      <ScrollView horizontal={true} style={styles.horizontalScroll}>
        {airSegments &&
          airSegments.map((s) => (
            <TouchableOpacity key={s.SegmentId} onPress={() => segmentSelectionHandler(s)}>
              <View
                style={[
                  styles.segmentPicker,
                  currentSegmentIndex === s.SegmentId && styles.segmentPickerActive,
                ]}
              >
                <BaseText
                  style={
                    currentSegmentIndex === s.SegmentId
                      ? styles.segmentTextActive
                      : styles.segmentText
                  }
                >
                  {s.DepartureAirportCode} - {s.ArrivalAirportCode}
                </BaseText>
              </View>
            </TouchableOpacity>
          ))}
      </ScrollView>
      <View style={CommonStyles.flex1}>
        {seatLoading && <ActivityIndicator size="small" />}
        {!seatLoading && (
          <>
            <View style={styles.dropdown}>
              {REBOOK_ENABLED ? (
                <PickerSelect
                  onValueChange={(value) => setActivePassenger(value)}
                  items={passengerDataForDropdown}
                  label="Select a seat for:"
                  value={activePassenger}
                />
              ) : null}
            </View>
            <View style={styles.dropdown}>
              {EVENTS_ENABLED ? (
                <PickerSelect
                  onValueChange={(value) => setEventsActivePassenger(value)}
                  items={eventsPassengerDataForDropdown}
                  label="Select a seat for:"
                  value={eventsActivePassenger}
                />
              ) : null}
            </View>

            {REBOOK_ENABLED || EVENTS_ENABLED
              ? seatSelection[currentSegmentIndex] &&
                passengerNamesKeys?.map((passenger, index) => (
                  <BaseText style={styles.selectedSeat} key={index}>
                    {getLabelForPassenger(passenger)}:{' '}
                    {seatSelection[currentSegmentIndex][passenger].SeatNumber}
                  </BaseText>
                ))
              : null}
            {(REBOOK_ENABLED || EVENTS_ENABLED) && multipleSelectSamePassenger ? (
              <BaseText style={styles.errorMesssage}>
                Cannot select same seat for multiple passengers.
              </BaseText>
            ) : null}
            {!seatMapNotAvailable && activeSeatMap && (
              <FlatList
                data={activeSeatMap}
                ListHeaderComponent={
                  <View style={CommonStyles.centerItems}>
                    <View style={CommonStyles.my2}>
                      <BaseText>
                        {activeSegment?.AirlineCode} #{activeSegment?.FlightNumber}
                      </BaseText>
                    </View>
                    <View style={CommonStyles.my2}>
                      <BaseText>
                        {t(tKey('Pick'))}{' '}
                        {REBOOK_ENABLED
                          ? getLabelForPassenger(activePassenger) ??
                            getLabelForPassenger(passengerDataForDropdown[0]?.value)
                          : EVENTS_ENABLED
                          ? eventsActivePassenger.split('_')[0] ??
                            eventsPassengerDataForDropdown[0]?.value?.split('_')[0]
                          : travelCart.Passengers[0].FirstName}
                        's {t(tKey('Seat'))}{' '}
                        {seatSelection[currentSegmentIndex] &&
                          seatSelection[currentSegmentIndex][0]?.SeatNumber}
                      </BaseText>
                    </View>
                    <View style={CommonStyles.my2}>
                      <BaseText>
                        {t(tKey('Coach'))} {activeSegment?.Cabin} {t(tKey('Class'))}
                      </BaseText>
                      <BaseText
                        style={[
                          styles.seatMapHeaderSectionText,
                          styles.seatMapHeaderSectionPriceText,
                        ]}
                      >
                        +
                        {formatCurrency(Number(seatTotal).toFixed(2), currency, currencyCulture, 2)}
                      </BaseText>
                    </View>
                  </View>
                }
                keyExtractor={seatRowKeyExtractor}
                renderItem={renderSeatRow}
                getItemLayout={getSeatItemLayout}
              />
            )}
            {seatMapNotAvailable && (
              <BaseText style={styles.seatMapNotAvailableText}>
                {t(tKey('SeatMapNotAvailable'))}
              </BaseText>
            )}
          </>
        )}
      </View>
      <FixedNextButtonBar
        cartTotal={cartTotal}
        onPress={() => {
          (REBOOK_ENABLED || EVENTS_ENABLED) && seatSelection[currentSegmentIndex]
            ? dispatch(updatePassengersSeats(seatSelection[currentSegmentIndex]))
            : null;
          props.navigation.navigate(Screens.Purchase);
        }}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  seatTypeSection: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    ...CommonProperties.mb3,
  },
  box: {
    height: '20px',
    width: '20px',
  },
  mainCabinExtra: {
    backgroundColor: Color.orange,
  },
  preferred: { backgroundColor: Color.green },
  available: { backgroundColor: Color.primary },
  unavailable: { backgroundColor: Color.gray },
  flex1: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  stepperContainer: {
    ...CommonProperties.mt2,
    ...CommonProperties.mt3,
    ...CommonProperties.mx2,
  },
  seatMapHeaderSectionPriceText: {
    textAlign: 'center',
    color: Color.primary,
  },
  segmentPicker: {
    borderColor: Color.primary,
    borderWidth: 1,
    ...CommonProperties.m2,
    ...CommonProperties.py2,
    ...CommonProperties.px3,
    ...CommonProperties.borderRadius,
  },
  segmentPickerActive: {
    borderColor: Color.active,
    backgroundColor: Color.active,
  },
  segmentText: {
    color: Color.primary,
  },
  segmentTextActive: {
    color: Color.accent,
  },
  seatRow: {
    height: seatRowHeight,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    ...CommonProperties.p1,
  },
  setRowNewService: {
    ...CommonProperties.mt5,
  },
  dropdown: {
    ...CommonProperties.pl2,
    ...CommonProperties.pr2,
  },
  exitRow: {
    backgroundColor: 'rgba(200,200,200,0.2)',
    borderRightColor: Color.required,
    borderLeftColor: Color.required,
    borderRightWidth: 2,
    borderLeftWidth: 2,
    marginBottom: 2,
  },
  aisleFiller: {
    flex: 0.2,
  },
  seatMapNotAvailableText: {
    color: 'red',
    textAlign: 'center',
  },
  horizontalScroll: {
    maxHeight: scale(40),
  },
  selectedSeat: {
    ...CommonProperties.ml2,
  },
  errorMesssage: {
    ...CommonProperties.ml2,
    color: 'red',
  },
  priceContainer: {
    flex: 0.2,
  },
  totalPriceContainer: {
    flex: 1,
  },
});

export default SeatScreen;
